import type { FeatureFlag } from '@partful/shared-types';
import { Box3, Object3D, Vector3, Vector3Tuple } from 'three';

export const CAMERA_POSITION_OFFSET = new Vector3(-4, 1, -2);

export const cameraIsStatic = (enabledFeatures: FeatureFlag[]) => enabledFeatures.includes('configuration-management');

export function getDefaultPosition(
  position: Vector3Tuple | undefined,
  centerOfAScene: Vector3,
  modelSize: Vector3,
  enabledFeatures: FeatureFlag[]
) {
  if (cameraIsStatic(enabledFeatures)) {
    return modelSize.multiplyScalar(2).toArray();
  }

  return position ?? new Vector3().addVectors(centerOfAScene, CAMERA_POSITION_OFFSET).toArray();
}

export function getOrbitCenter(
  orbitCenter: Vector3Tuple | undefined,
  centerOfAScene: Vector3,
  enabledFeatures: FeatureFlag[]
) {
  if (cameraIsStatic(enabledFeatures)) {
    return centerOfAScene.toArray();
  }

  return orbitCenter ?? centerOfAScene.toArray();
}

export function applySceneScaling(scene: Object3D, enabledFeatures: string[]) {
  const scaling = enabledFeatures.includes('configuration-management')
    ? false
    : normalizedBlenderPluginFlag(scene, 'applySvtScaling');

  return scaling;
}

export function getModelSize(modelBoundingBox?: Box3) {
  const modelSize = modelBoundingBox ? modelBoundingBox.getSize(new Vector3()) : new Vector3(2, 2, 2);
  const sceneMagnitude = modelSize.length();
  return { modelSize, sceneMagnitude };
}

/* istanbul ignore next */
export function resetScene(scene: Object3D) {
  scene.removeFromParent(); // remove all transformation (scaling, etc.) applied to the group wrapper
}

export function normalizedBlenderPluginFlag(scene: Object3D, blenderPluginflagName: string) {
  /* istanbul ignore next */
  // Flags set in blender plugin are numbers, 0 or 1. They can also be missing in models exported before a particular flag was introduced.
  const {
    userData: {
      svtGltfExportProps = {}, // For older models the wrapper containing our custom props may be undefined as well.
    },
  } = scene;

  return svtGltfExportProps[blenderPluginflagName] !== 0;
  // Here we assume that the flag is true if undefined or not explicitylu set to 0
  // Only old legacy models will have undefined flags
}
